import React, { useState, useEffect, useRef } from 'react';
import { Sprout, Send, Sparkles, AlertTriangle, RefreshCw, MessageSquare, BrainCircuit, CornerDownLeft, X } from 'lucide-react';

const CropAIConsultant = ({ selectedCropContext, cropsList = [], onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedCropId, setSelectedCropId] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Quick prompt suggestions
  const quickSuggestions = [
    { text: '¿Cómo diagnosticar y controlar la mosca blanca?', label: 'Mosca Blanca' },
    { text: 'Deficiencias de Nitrógeno: Síntomas y soluciones', label: 'Falta Nitrógeno' },
    { text: 'Diseña un plan de riego eficiente para Maíz en clima seco', label: 'Riego Maíz' },
    { text: 'Control ecológico de plagas con bioinsumos', label: 'Bioinsumos' }
  ];

  // Load chat history from localStorage for offline persistence
  useEffect(() => {
    const savedChat = localStorage.getItem('sisciac_ai_chat');
    if (savedChat) {
      setMessages(JSON.parse(savedChat));
    } else {
      // Add welcome message
      setMessages([
        {
          id: 'welcome',
          role: 'assistant',
          text: '¡Hola! Soy tu **Consultor Agrícola Inteligente SISCIAC**, impulsado por **Gemini 2.5**. \n\nPuedo ayudarte a diagnosticar plagas, deficiencias de nutrientes, diseñar planes de riego y optimizar tus cosechas. Selecciona un cultivo para contextualizar mi respuesta o hazme cualquier pregunta técnica directa.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    }
  }, []);

  // Save chat history to localStorage
  const saveChatHistory = (updatedMessages) => {
    setMessages(updatedMessages);
    localStorage.setItem('sisciac_ai_chat', JSON.stringify(updatedMessages));
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSendMessage = async (textToSend) => {
    const text = textToSend || inputValue;
    if (!text.trim()) return;

    if (!textToSend) {
      setInputValue('');
    }

    const newUserMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    const updatedMessages = [...messages, newUserMessage];
    saveChatHistory(updatedMessages);
    setIsTyping(true);

    // Contextual crop info
    let cropContext = null;
    const cropToContextualize = cropsList.find(c => c.id === parseInt(selectedCropId || selectedCropContext?.id));
    if (cropToContextualize) {
      cropContext = {
        name: cropToContextualize.name,
        variety: cropToContextualize.variety || 'No especificada',
        area: cropToContextualize.area,
        status: cropToContextualize.status,
        planting_date: cropToContextualize.planting_date
      };
    }

    try {
      const response = await fetch('/api/ai/consult', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sisciac_token')}`
        },
        body: JSON.stringify({
          message: text,
          cropContext: cropContext,
          history: updatedMessages.slice(-6).map(m => ({ role: m.role, text: m.text }))
        })
      });

      const data = await response.json();
      
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: data.response || 'Disculpa, no he podido procesar tu solicitud en este momento.',
        isDemo: data.mode === 'demo',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      saveChatHistory([...updatedMessages, assistantMessage]);
    } catch (error) {
      console.error('Error consulting AI:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: 'Lo siento, ha ocurrido un error de conexión con la inteligencia artificial. Por favor, inténtalo de nuevo.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      saveChatHistory([...updatedMessages, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const clearChat = () => {
    if (window.confirm('¿Quieres limpiar el historial de conversación con el consultor de IA?')) {
      localStorage.removeItem('sisciac_ai_chat');
      setMessages([
        {
          id: 'welcome',
          role: 'assistant',
          text: 'Conversación reiniciada. ¿En qué cultivo o problema agrícola nos enfocaremos hoy?',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    }
  };

  // Simple safe markdown-to-html formatter for bold, italic, list items and double newlines
  const formatMarkdown = (text) => {
    if (!text) return '';
    let formatted = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Bold (**text** or __text__)
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-emerald-950">$1</strong>');
    
    // Bullet points (* text or - text)
    formatted = formatted.replace(/^\s*[\*\-]\s+(.*?)$/gm, '<li class="ml-4 list-disc text-gray-700 my-1">$1</li>');

    // Replace double newlines with paragraph tags
    formatted = formatted.split('\n\n').map(p => {
      if (p.includes('<li')) {
        return `<ul class="my-2">${p}</ul>`;
      }
      return `<p class="mb-2 text-gray-700 leading-relaxed">${p.replace(/\n/g, '<br />')}</p>`;
    }).join('');

    return formatted;
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden shadow-lg" id="crop-ai-consultant-container">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-700 to-teal-800 p-4 flex items-center justify-between text-white shadow-md">
        <div className="flex items-center space-x-3">
          <div className="bg-white/15 p-2 rounded-xl">
            <BrainCircuit className="h-6 w-6 text-emerald-200" />
          </div>
          <div>
            <h3 className="font-bold text-base leading-tight flex items-center space-x-1.5">
              <span>Consultor de Cultivos IA</span>
              <Sparkles className="h-3.5 w-3.5 text-yellow-300 animate-pulse" />
            </h3>
            <p className="text-xs text-emerald-100 flex items-center space-x-1">
              <span className="h-1.5 w-1.5 bg-emerald-400 rounded-full inline-block animate-ping"></span>
              <span>Gemini 2.5 Flash Online</span>
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button 
            onClick={clearChat}
            className="p-1.5 rounded-lg hover:bg-white/10 text-emerald-100 hover:text-white transition-colors"
            title="Reiniciar chat"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {onClose && (
            <button 
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 text-emerald-100 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Selector de Contexto */}
      <div className="bg-white border-b border-slate-100 p-3 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="font-medium text-slate-600 flex items-center space-x-1">
          <Sprout className="h-3.5 w-3.5 text-emerald-600" />
          <span>Contexto del Cultivo:</span>
        </span>
        <select
          value={selectedCropId}
          onChange={(e) => setSelectedCropId(e.target.value)}
          className="bg-slate-50 border border-slate-200 rounded-lg py-1.5 px-2 text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 max-w-xs transition-shadow"
        >
          <option value="">-- Preguntar de manera general --</option>
          {cropsList.map(c => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.producer_name || 'Productor'})
            </option>
          ))}
        </select>
      </div>

      {/* Messages Feed */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm relative ${
                m.role === 'user'
                  ? 'bg-emerald-600 text-white rounded-br-none'
                  : 'bg-white text-slate-800 rounded-bl-none border border-slate-100'
              }`}
            >
              {m.role === 'assistant' && m.isDemo && (
                <span className="absolute -top-2.5 right-3 bg-yellow-100 text-yellow-800 border border-yellow-200 text-[9px] font-semibold px-1.5 py-0.5 rounded-full shadow-xs">
                  Modo Demo
                </span>
              )}
              {m.role === 'user' ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
              ) : (
                <div 
                  className="text-sm text-slate-700"
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(m.text) }}
                />
              )}
              <span
                className={`block text-[10px] mt-1 text-right ${
                  m.role === 'user' ? 'text-emerald-100' : 'text-slate-400'
                }`}
              >
                {m.timestamp}
              </span>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm text-slate-800 max-w-[85%] flex items-center space-x-2">
              <span className="text-xs text-slate-400">Analizando con IA</span>
              <div className="flex space-x-1">
                <span className="h-2 w-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="h-2 w-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="h-2 w-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Suggestions panel */}
      {messages.length < 3 && (
        <div className="px-4 py-2 bg-slate-100/50 border-t border-slate-200/50">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center space-x-1">
            <MessageSquare className="h-3 w-3" />
            <span>Consultas Rápidas Sugeridas:</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {quickSuggestions.map((s, idx) => (
              <button
                key={idx}
                onClick={() => handleSendMessage(s.text)}
                className="bg-white hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 text-slate-700 hover:text-emerald-800 rounded-lg px-2 py-1 text-xs text-left transition-all shadow-2xs"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Form */}
      <div className="p-3 bg-white border-t border-slate-200 flex items-center space-x-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Describe plagas, síntomas de hojas, suelo..."
          className="flex-1 bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
        />
        <button
          onClick={() => handleSendMessage()}
          disabled={!inputValue.trim() || isTyping}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 text-white p-2 rounded-xl transition-all shadow-md active:scale-95 disabled:scale-100 flex items-center justify-center cursor-pointer disabled:cursor-not-allowed"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default CropAIConsultant;
